import { Check, ClearOutlined, DeleteOutline, EditOutlined } from '@mui/icons-material';
import {
  GridActionsCellItem,
  DataGridProProps,
  GridRowModes,
  GridRowId,
  GridApi,
  GridRowParams,
} from '@mui/x-data-grid-pro';
import { MutableRefObject, ReactElement, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { I18nNamespace } from '~/common/types';
import { UserProfileAbsenceDataGridField } from '~/common/types/UserProfileAbsenceDataGrid';
import { gridColDefFactory } from '~/components/DataGridCustom';
import { useGridApiRowModelActions } from '~/hooks/useGridApiRowModelActions/useGridApiRowModelActions';

import { AbsenceGridRow } from './UserProfileAbsenceGridForm.types';

type UseAbsenceGridActionsProps = {
  editable: boolean;
  apiRef: MutableRefObject<GridApi>;
  onDelete: (id: GridRowId) => void;
};

type UseAbsenceGridActionsResult = Pick<DataGridProProps<AbsenceGridRow>, 'rowModesModel' | 'onRowModesModelChange'> & {
  actionColumn: DataGridProProps<AbsenceGridRow>['columns'] | undefined;
  addRow: () => void;
};

enum Actions {
  SAVE = 'save',
  CANCEL = 'cancel',
  EDIT = 'edit',
  DELETE = 'delete',
}

interface ActionDefinition {
  label: Actions;
  icon: ReactElement<any>;
}

const editModeActions: { [K in GridRowModes]: ActionDefinition[] } = {
  [GridRowModes.Edit]: [
    {
      label: Actions.SAVE,
      icon: <Check />,
    },
    {
      label: Actions.CANCEL,
      icon: <ClearOutlined />,
    },
  ],
  [GridRowModes.View]: [
    {
      label: Actions.EDIT,
      icon: <EditOutlined />,
    },
    {
      label: Actions.DELETE,
      icon: <DeleteOutline />,
    },
  ],
};

export const useUserProfileAbsenceGridActions = ({
  apiRef,
  onDelete,
  editable,
}: UseAbsenceGridActionsProps): UseAbsenceGridActionsResult => {
  const { t } = useTranslation();
  const { t: tColumn } = useTranslation(I18nNamespace.widgets, {
    keyPrefix: 'UserProfileAbsenceWidget.datagrid.column',
  });
  const { t: tActions } = useTranslation(I18nNamespace.widgets, {
    keyPrefix: 'UserProfileAbsenceWidget.actions',
  });

  const { addRow, editRow, cancelRow, deleteRow, saveRow, rowModesModel, setRowModesModel } = useGridApiRowModelActions(
    { apiRef, scrollIntoView: true },
  );

  const actionColumn = useMemo(() => {
    const createColumn = gridColDefFactory({ tColumn, t, defaultOptions: { sortable: false } });

    const handleEditClick = (id: GridRowId) => () => {
      editRow(id);
    };

    const handleSaveClick = (id: GridRowId) => () => {
      saveRow(id);
    };

    const handleDeleteClick = (id: GridRowId) => async () => {
      await onDelete(id);
      deleteRow(id);
    };

    const handleCancelClick = (id: GridRowId) => () => {
      cancelRow(id);
    };

    const actionHandlerLookup: { [K in Actions]: (id: GridRowId) => () => void } = {
      [Actions.SAVE]: handleSaveClick,
      [Actions.CANCEL]: handleCancelClick,
      [Actions.EDIT]: handleEditClick,
      [Actions.DELETE]: handleDeleteClick,
    };

    const getActions = ({ id }: GridRowParams<any>) => {
      const rowMode = apiRef.current?.getRowMode(id);
      return editModeActions[rowMode].map(({ label, icon }) => {
        const handler = actionHandlerLookup[label];
        return (
          <GridActionsCellItem
            key={label}
            icon={icon}
            label={tActions(label)}
            onClick={handler(id)}
            className="textPrimary"
            color="inherit"
          />
        );
      });
    };

    return [
      createColumn({
        field: UserProfileAbsenceDataGridField.ACTIONS,
        type: 'actions',
        minWidth: 88, // Size of two actions buttons + padding + (optional) scrollbar
        headerAlign: 'center',
        align: 'left',
        resizable: false,
        getActions,
      }),
    ];
  }, [tColumn, t, editRow, saveRow, deleteRow, onDelete, cancelRow, apiRef, tActions]);

  return {
    actionColumn: editable ? actionColumn : undefined,
    rowModesModel,
    onRowModesModelChange: setRowModesModel,
    addRow,
  };
};
